import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiClient';

interface UseMetricsProps {
  config: any;
  storeId: string | null;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

// Función para validar y limpiar datos
const validateAndCleanData = (data: any, configId: string) => {
  if (!data) return data;
  
  // Si es un array, validar cada elemento
  if (Array.isArray(data)) {
    const cleanedData = data.filter(item => {
      if (!item || typeof item !== 'object') return false;
      
      // Verificar que tenga al menos una propiedad de valor válida
      const hasValidValue = Object.values(item).some(value => {
        if (typeof value === 'string' && value === 'name') return true; // Permitir propiedades de nombre
        if (typeof value === 'string' && value === 'date') return true; // Permitir propiedades de fecha
        if (typeof value === 'string' && value === 'reason') return true; // Permitir propiedades de razón
        const numValue = typeof value === 'number' ? value : parseFloat(String(value));
        return !isNaN(numValue) && numValue >= 0 && isFinite(numValue);
      });
      
      if (!hasValidValue) {
        console.warn(`⚠️ Item filtrado en useMetrics para ${configId}:`, item);
      }
      
      return hasValidValue;
    });
    
    // Validación específica para gráficos de CyD
    if (configId === 'cyd-by-reason' || configId === 'cyd-trend') {
      const finalData = cleanedData.map(item => {
        if (configId === 'cyd-by-reason') {
          return {
            ...item,
            cambiar: Math.max(0, item.cambiar || 0), // Permitir 0 en porcentajes
            devolver: Math.max(0, item.devolver || 0), // Permitir 0 en porcentajes
            total: Math.max(0, item.total || 0) // Permitir 0 en porcentajes
          };
        } else if (configId === 'cyd-trend') {
          return {
            ...item,
            value: Math.max(0.01, item.value || 0)
          };
        }
        return item;
      });
      
      console.log(`📊 Datos validados para ${configId}:`, finalData);
      return finalData;
    }
    
    console.log(`📊 Datos validados para ${configId}:`, cleanedData);
    return cleanedData;
  }
  
  // Si es un objeto con value, validar el value
  if (data.value !== undefined) {
         const numValue = typeof data.value === 'number' ? data.value : parseFloat(String(data.value));
    if (isNaN(numValue) || numValue < 0 || !isFinite(numValue)) {
      console.warn(`⚠️ Valor inválido en useMetrics para ${configId}:`, data);
      return { ...data, value: 0 };
    }
    
    // Validación específica para la tasa de CyD
    if (configId === 'cyd-rate') {
      const roundedValue = Math.round(numValue * 100) / 100;
      return { ...data, value: roundedValue };
    }
  }
  
  return data;
};

export const useMetrics = ({ config, storeId, startDate, endDate, enabled = true }: UseMetricsProps) => {
  return useQuery({
    queryKey: [config.id, storeId, startDate, endDate],
    queryFn: async () => {
      if (!storeId) throw new Error('Store ID is required');
      
      if (!config.apiEndpoint) {
        throw new Error(`No API endpoint configured for metric: ${config.id}`);
      }
      
      console.log(`🚀 Starting fetch for ${config.id} (${config.type})`);
      console.log(`📊 Config:`, { storeId, startDate, endDate, endpoint: config.apiEndpoint });
      
      try {
        // Obtener datos del período actual
        let currentEndpoint = config.apiEndpoint.replace('{storeId}', storeId);
        if (startDate && endDate) {
          const url = new URL(currentEndpoint, process.env.REACT_APP_API_URL || '');
          url.searchParams.set('startDate', startDate);
          url.searchParams.set('endDate', endDate);
          currentEndpoint = url.pathname + url.search;
        }
        
        console.log(`🔍 Fetching current data from: ${currentEndpoint}`);
        console.log(`📅 Date range being sent: ${startDate} to ${endDate}`);
        
        const currentResponse = await apiService.get(currentEndpoint);
        const currentData = currentResponse.data;
        
        console.log(`📥 Current data received for ${config.id}:`, currentData);
        console.log(`📊 Response structure:`, {
          success: (currentResponse as any).success,
          dataType: typeof currentData,
          isArray: Array.isArray(currentData),
          hasValue: (currentData as any)?.value !== undefined,
          value: (currentData as any)?.value
        });
        
        // Validar y limpiar los datos
        const validatedData = validateAndCleanData(currentData, config.id);
        
        console.log(`✅ Final result for ${config.id}:`, validatedData);
        return validatedData;
      } catch (error) {
        console.error(`❌ Error in useMetrics for ${config.id}:`, error);
                 console.error(`❌ Error details:`, {
           message: (error as any).message,
           status: (error as any).response?.status,
           data: (error as any).response?.data
         });
        throw error;
      }
    },
    enabled: enabled && !!storeId && !!config.apiEndpoint && !!startDate && !!endDate,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    retryDelay: 1000,
    staleTime: 10 * 60 * 1000, // Los datos se consideran frescos por 10 minutos
    gcTime: 15 * 60 * 1000, // Mantener en caché por 15 minutos
  });
}; 